import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => (slug ? getArticleBySlug(slug) : undefined), [slug]);
  const relatedArticles = useMemo(() => (article ? getRelatedArticles(article) : []), [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-search-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="font-heading font-bold text-xl text-foreground-800 mb-2">文章未找到</h2>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm whitespace-nowrap cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${article.title} - 黄瓜网站 | 黄瓜${article.categoryName}`}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.huanggua.site/article/${article.slug}`}
        ogType="article"
      />

      <Navbar />
      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.categoryName, href: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />
      </div>

      <main className="w-full px-4 md:px-6 py-6 md:py-10">
        <div className="max-w-4xl mx-auto">
          <article>
            <header className="mb-6 md:mb-8">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-xs text-primary-600 bg-primary-100 px-2.5 py-1 rounded-md font-medium hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {article.categoryName}
                </Link>
                {article.tags.map((tag, i) => (
                  <span key={i} className="text-[10px] text-secondary-700 bg-secondary-100 px-2 py-0.5 rounded-md whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading font-bold text-xl md:text-3xl text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-xs md:text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-user-line text-primary-600 text-xs"></i>
                  </div>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime} 分钟阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.viewCount.toLocaleString()} 次浏览
                </span>
              </div>
            </header>

            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-60 md:h-96 object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:mt-8 prose-headings:mb-4
                prose-h2:text-lg md:prose-h2:text-xl prose-h2:font-bold
                prose-h3:text-base md:prose-h3:text-lg prose-h3:font-semibold
                prose-p:mb-4 prose-p:text-sm md:prose-p:text-base
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">标签：</span>
                {article.tags.map((tag, i) => (
                  <span key={i} className="text-xs bg-secondary-100 text-secondary-700 px-2.5 py-1 rounded-md whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200">
              <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-900 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {rel.title}
                      </h3>
                      <div className="flex items-center gap-2 text-[11px] text-foreground-400">
                        <span>{rel.publishDate}</span>
                        <span>·</span>
                        <span>{rel.readTime}分钟阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: article.title,
                description: article.summary,
                author: { '@type': 'Person', name: article.author },
                datePublished: article.publishDate,
                image: article.coverImage,
                articleSection: article.categoryName,
                keywords: article.tags.join(','),
                inLanguage: 'zh-CN',
              }),
            }}
          />
        </div>
      </main>

      <Footer />
    </>
  );
}